import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Ellipsis } from 'lucide-react';
import {
  AutosizeTextarea,
  ChoiceSelect,
  HelpPopover,
  NotPermitted,
  OffReason,
  ReasonDialog,
  RecordSection,
  StatusChip
} from '@/design';
import { ApiError, ProblemType, type IEtagged } from '@/domains/api';
import { useAuth, useCan } from '@/domains/auth';
import { ddApi } from '../../api/dd-api';
import type {
  DdMeetingKind,
  IDdInterview,
  IDdNotesPreview,
  IDdSection,
  IOutreachBody,
  IOutreachAttempt,
  OutreachChannel,
  OutreachPurpose
} from '../../api/dd-contracts';
import type { IInternalFormSchema, IParticipantInternalRecord } from '../../api/internal-field-contracts';
import type { IOversightResponseRow } from '../../api/oversight-contracts';
import { useAdminData } from '../../components/admin-page';
import { openFolds } from '../../components/section-nav';
import {
  DD_EMAILS_BEFORE_CALL,
  DD_MEETING_KINDS,
  IN_PERSON_ROUNDS,
  OTHER_MEETING_KINDS,
  OUTREACH_CHANNELS,
  OUTREACH_PURPOSES,
  U4_FLAGS,
  callOverdue,
  currentResponse,
  ddStep,
  ddWhen,
  fromLocalInput,
  outreachWord,
  type IDdDisclosures,
  type IDdStep,
  meetingKindOf,
  meetingLabel,
  qualityFieldCode,
  qualityLabel,
  reasonOptions,
  scoreOptions,
  u4FlagOf,
  type U4Flag
} from '../../dd';
import { REMOVED_ACCOUNT, countOf } from '../../lists';
import { REFRESH_OF_ID_TYPE } from '../../registries';
import { refusalMessage } from '../../refusal';
import { DdOverdueChip, DdStatusChip, U4FlagLink } from '../dd/dd-parts';
import { ExceptionDialog, staffFieldException, type DdExceptionWrite } from './dd-exception';
import { ScheduleDialog, noCycleRefusal, type IDdBooking } from './dd-schedule';
import { HUB_MEMORY_PAGE } from './hub-memory';
import { RefreshControl, type INumberNote } from './numbers';
import type { IResearchPart, IResearchPartSection } from './research';

/**
 * Due diligence inside each Veritant research part (SR-314 / SR-324; survey-api's due-diligence
 * design §7), laid out by the step the advisor is at (SR-525, C1): the status with the one primary
 * button `ddStep` names — the same step the DD calls queue and the record's chip say — then the
 * contact (the outreach log, the quick logs and the DD call's booking), what is ready for the call
 * (the U4 check and *Confirm clear for DD*), every meeting of the cycle in one table, and the past
 * cycles. Explanations sit under "?", and why a press is off in its tooltip (C3).
 *
 * **The interview opens in a sheet**: paste from Granola or type by hand first, its template's
 * sections, and a footer that stays in view with the Quality score, *Save draft* and *Complete*
 * (RNK-16 AC1). A completion writes the score into Quality rating and sets the DD call flag in
 * survey-api's one transaction, so the part's fields are read again after it, as after every write
 * here (`writes.reread`). The draft saves itself ~1.5 s after typing stops, under the revision
 * held; survey-api folds an author's quiet saves into one History event. *Paste* splits the
 * note-taker's text by its headings — a preview first, written only on *Apply* (RNK-16 AC2).
 *
 * **An exception** (unresponsive, not an FA, rude) is the Quality rating's reason, which survey-api
 * turns into Travel NO: *Mark as exception…* in the "⋯" menu and *Couldn't complete…* in the DD
 * call sheet's footer open one dialog (`dd-exception.tsx`), written by the host.
 *
 * survey-api reads the block under `internal_fields.read` and writes it under
 * `internal_fields.write`; a viewer without the latter reads it and is told why it does not offer
 * a write. A revision moved by another save is said and re-read, never overwritten.
 *
 * **Every meeting is booked through one *Schedule…*** (SR-525, A2: `dd-schedule.tsx`), the DD call
 * with its interviewer too — the block's one, under the meetings table, which also moves the DD
 * call's booking; a meeting's is moved with *Reschedule…* in its sheet, which draws the date and
 * it does not edit it. A booked DD call is said in the contact part with who set it, so nobody
 * sends the advisor another DD email (SR-424); a time passed with the call still open is Overdue.
 *
 * **The disclosures are checked before the call** (SR-460): the U4 check (*Run U4 check*, the
 * numbers' own press) and *Confirm clear for DD*, a recorded fact on this cycle's interview that
 * survey-api refuses while a compliance item is open. Its facts are the host's, already read — the
 * queue's row in DD calls, the hub's summary and Work items' read on the record page — never read
 * again here. *Complete the DD call* without a clearance, or with an item open, asks first; it does
 * not refuse.
 *
 * **One block, two hosts**: `DdBlock` is drawn here — a fold in the part, a section of the tab on
 * a survey tab (SR-474) — and beside the queue in
 * Work → DD calls' side panel, so the interview and the outreach are never drawn twice over. A DD
 * calls row's link lands on this fold (`IDdRevealState`), opened and scrolled to once.
 */
export function ddPartSection(
  disclosuresOf: (participantId: string) => IDdDisclosures | undefined,
  /** The hub's staff-field records — the Quality rating's version an exception quotes. */
  internal?: readonly IParticipantInternalRecord[]
): IResearchPartSection {
  return {
    key: 'dd',
    title: 'Due diligence',
    sourceKind: 'dd',
    anchorOf: ddAnchor,
    render: (part) => (
      <DdSection part={part} disclosures={disclosuresOf(part.member.participantId)} internal={internal ?? []} />
    )
  };
}

/** The record page's place for one participant's due diligence of one survey — the fold's `id`,
 *  which a link from Work → DD calls asks the page to open on arrival (`IDdRevealState`). */
export function ddAnchor(participantId: string, surveyCode: string): string {
  return `dd-${participantId}-${surveyCode}`;
}

/** What a link to a record's survey tab carries to land on its Due diligence block: `ddAnchor`'s
 *  id. Its own member, apart from a disclosure's `reveal`, whose hook would take the block for a
 *  disclosure. */
export interface IDdRevealState {
  revealDd: string;
}

/** The fragment a link from another tab carries to land on the record's Due diligence block — DD
 *  calls' ↗: a new tab holds no router state, so the address says it. Dropped on arrival. */
export const DD_HASH = '#due-diligence';

/** The block a DD calls link asked for, between the entry's replacement and its drawing. */
let pendingReveal: Nullable<string> = null;

/** What ends `revealAndHold` at once: the person taking the page over. */
const TAKEOVER_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;
/** How long `revealAndHold` keeps the block in view while the page is still drawing — a record
 *  page on a loaded server answers its last read well after the first — and how often it looks. */
const HOLD_MS = 15_000;
const HOLD_EVERY_MS = 100;

/** The hold under way, if any — one at a time, a later reveal replacing it. */
let stopHold: Nullable<() => void> = null;

/**
 * Scrolls the block `anchor` names to the top of the view and keeps it there while the record
 * page is still drawing above it. The sections before Due diligence read on their own and grow
 * after the reveal — Shook research's schema, the Surveys and Records reads — and the block is
 * drawn again as they land; scrolled once, it was pushed out of view under the person who asked
 * for it (SR-477: the DD calls spec saw its summary focused at a viewport ratio of 0). So the
 * block is found by its id on each look — a node held would be the one a redraw replaced — and
 * moved, it is scrolled back: for `HOLD_MS` at most, and never after the person takes the page
 * over with a wheel, a touch, a key or a press. A scroll alone is not read as theirs: the page
 * growing moves the frame's offset too (the browser's scroll anchoring), and a hold that stopped
 * on it stopped at once (gate 2's attempt). Not tied to the section's life: a redraw would end it.
 */
function revealAndHold(anchor: string): void {
  stopHold?.();
  let top: Nullable<number> = null;
  function reveal(): void {
    const node = document.getElementById(anchor);
    node?.scrollIntoView({ block: 'start' });
    top = node?.getBoundingClientRect().top ?? null;
  }
  function stop(): void {
    window.clearInterval(watch);
    window.clearTimeout(timer);
    for (const event of TAKEOVER_EVENTS) window.removeEventListener(event, stop, true);
    if (stopHold === stop) stopHold = null;
  }
  reveal();
  const watch = window.setInterval(() => {
    const now = document.getElementById(anchor)?.getBoundingClientRect().top;
    if (now !== undefined && (top === null || Math.abs(now - top) >= 1)) reveal();
  }, HOLD_EVERY_MS);
  const timer = window.setTimeout(stop, HOLD_MS);
  for (const event of TAKEOVER_EVENTS) window.addEventListener(event, stop, true);
  stopHold = stop;
}

function revealDdOf(state: unknown): Nullable<string> {
  const reveal = (state as Partial<IDdRevealState> | null)?.revealDd;
  return typeof reveal === 'string' && reveal !== '' ? reveal : null;
}

/**
 * The Quality rating's version as the page read it, for the exception's `If-Match`: the value on
 * the response "this cycle" is — a `participant_cycle` value under its ranking cycle, any other
 * scope among the survey's values — and 0 for an empty cell, as survey-api reads one.
 */
function qualityVersionOf(
  internal: readonly IParticipantInternalRecord[],
  participantId: string,
  surveyCode: string,
  response: IOversightResponseRow,
  code: string
): number {
  const survey = internal
    .find((record) => record.participantId === participantId)
    ?.surveys.find((one) => one.surveyCode === surveyCode);
  const value =
    survey?.cycles.find((cycle) => cycle.cycleCode === response.cycle.code)?.values.find((one) => one.field === code) ??
    survey?.values.find((one) => one.field === code && (!one.responseId || one.responseId === response.responseId));
  return value?.version ?? 0;
}

function DdSection({
  part,
  disclosures,
  internal
}: {
  part: IResearchPart;
  disclosures?: IDdDisclosures;
  internal: readonly IParticipantInternalRecord[];
}) {
  const { member, surveyCode, writes, responses, schema } = part;
  const anchor = ddAnchor(member.participantId, surveyCode);
  const response = currentResponse(responses);
  // An exception is written into the part's own Quality rating, on the response "this cycle" is.
  const quality = schema ? qualityFieldCode(schema) : null;
  const exception =
    response && quality
      ? staffFieldException(
          response.responseId,
          quality,
          qualityVersionOf(internal, member.participantId, surveyCode, response, quality),
          () => void writes.reread()
        )
      : undefined;

  // Opened from a DD calls row: once. The entry's state drops the request first — the record's
  // tabs carry the state from one to the next and remount this tab, and a reload reads it again,
  // so a request kept would pull the page back here each time — and the block is revealed under
  // the entry that no longer holds it, since replacing the entry moves the focus. It scrolls and
  // never navigates to a fragment, the section nav's rule. `pendingReveal` outlives a remount.
  const location = useLocation();
  const navigate = useNavigate();
  const asked = revealDdOf(location.state);
  // From a new tab (`DD_HASH`): the record's own part, the one whose participant the address names.
  const hashed = location.hash === DD_HASH && location.pathname.includes(`/${member.participantId}/`);
  React.useEffect(() => {
    if (hashed) {
      pendingReveal = anchor;
      navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: location.state });
      return;
    }
    if (asked === anchor) {
      pendingReveal = anchor;
      const { revealDd: _used, ...rest } = location.state as IDdRevealState;
      navigate(location, { replace: true, state: Object.keys(rest).length > 0 ? rest : null });
      return;
    }
    if (pendingReveal !== anchor) return;
    pendingReveal = null;
    const fold = document.getElementById(anchor);
    if (!fold) return;
    openFolds(fold);
    fold.querySelector<HTMLElement>(':scope > summary')?.focus({ preventScroll: true });
    revealAndHold(anchor);
  }, [asked, anchor, hashed]);

  return (
    <RecordSection
      page={HUB_MEMORY_PAGE}
      sectionKey={`dd.${surveyCode}`}
      title="Due diligence"
      level={part.section ? 2 : 4}
      defaultOpen
      filled={0}
      total={0}
      anchor={anchor}
    >
      <DdBlock
        participantId={member.participantId}
        surveyCode={surveyCode}
        name={member.displayName}
        firm={member.profile.firmName}
        cycle={response?.cycle.code ?? null}
        schema={schema}
        disclosures={disclosures}
        exception={exception}
        onChanged={() => void writes.reread()}
      />
    </RecordSection>
  );
}

/**
 * The block itself — the step, the contact, the call's readiness, the cycle's meetings, past
 * cycles and the interview's sheet — drawn in the record page's fold and in Work → DD calls' side
 * panel alike, so the two are one rendering. `onChanged` is the host's re-read after a write here:
 * the hub's on the record page (a completion moves Quality rating and the DD call flag), the
 * queue's in the panel.
 */
export function DdBlock({
  participantId,
  surveyCode,
  name,
  firm = null,
  cycle,
  schema,
  disclosures,
  exception,
  onChanged,
  onCompleted,
  version = 0,
  heading: Heading = 'h5',
  checkAbove = false
}: {
  participantId: string;
  surveyCode: string;
  name: string;
  /** The advisor's firm, for the sheet's context strip; null where the host holds none. */
  firm?: Nullable<string>;
  /** "This cycle": the newest live response's, the cycle the DD list reads — never the newest
   *  interview's, which after a new response is last cycle's. Null with no live response. */
  cycle: Nullable<string>;
  schema: Nullable<IInternalFormSchema>;
  /** The Disclosures line's facts (SR-460), the host's own reads; absent while it holds none,
   *  which draws no line. */
  disclosures?: IDdDisclosures;
  /** How the host writes an exception's reason into Quality rating (`dd-exception.tsx`); absent,
   *  *Mark as exception…* is not offered. */
  exception?: DdExceptionWrite;
  onChanged: () => void;
  /** Told when an interview of the block is completed here, with the revision to reopen it under
   *  — DD calls' *Undo*. */
  onCompleted?: (interview: IDdInterview, etag: string) => void;
  /** Moved by the host to have the block read again — after an *Undo* outside it. */
  version?: number;
  /** The block's own headings: under a part's level-4 fold on the record page, under the panel's
   *  `h2` in the queue. */
  heading?: 'h3' | 'h5';
  /** The host draws the U4 check above the block (the DD calls panel's header): the block's
   *  readiness part keeps only the clearance. */
  checkAbove?: boolean;
}) {
  const can = useCan();
  const writable = can('internal_fields.write');
  const notPermitted = `dd-not-permitted-${surveyCode}`;
  const primaryOff = React.useId();
  const blockRef = React.useRef<HTMLDivElement>(null);
  // Read by the block's own writes alone: another block's write moves nothing kept here.
  const interviews = useAdminData(
    (signal) => ddApi.interviews(participantId, surveyCode, signal),
    [participantId, surveyCode, version]
  );
  const outreach = useAdminData(
    (signal) => ddApi.outreach(participantId, surveyCode, signal),
    [participantId, surveyCode, version]
  );
  const [open, setOpen] = React.useState<Nullable<string>>(null);
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const [told, setTold] = React.useState<Nullable<string>>(null);
  const [busy, setBusy] = React.useState(false);
  const [scheduling, setScheduling] = React.useState<Nullable<DdMeetingKind>>(null);
  const [marking, setMarking] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);

  // SR-501: the cycle's DD call is one interview among its meetings — the follow-up calls and the
  // in-person rounds; earlier cycles' of every kind stay together below.
  const list = interviews.data ?? [];
  const isCall = (interview: IDdInterview) => meetingKindOf(interview) === 'dd_call';
  const current = list.find((interview) => isCall(interview) && interview.cycle === cycle) ?? null;
  const meetings = list.filter((interview) => !isCall(interview) && interview.cycle === cycle);
  const earlier = list.filter((interview) => interview !== current && !meetings.includes(interview));
  const reasons = schema ? reasonOptions(schema) : [];

  // This cycle's call is still to make: an interview read, a cycle, and its call not completed.
  const callOpen = interviews.data !== null && cycle !== null && current?.status !== 'completed';
  const booked = callOpen ? (current?.scheduledAt ?? null) : null;
  const attempts = outreach.data ?? [];
  const cadence = attempts.filter((attempt) => attempt.purpose === 'dd' && cycle !== null && attempt.cycle === cycle);
  // The soonest open booking among the other meetings — what `ddStep` weighs against the call.
  // Open as survey-api's `dd.next_meeting_at` reads it for the queue and the chip: in progress or
  // awaiting review, with a date.
  const nextMeeting =
    meetings
      .filter((meeting) => (meeting.status === 'in_progress' || meeting.status === 'pending_review') && meeting.scheduledAt)
      .sort((a, b) => Date.parse(a.scheduledAt!) - Date.parse(b.scheduledAt!))[0] ?? null;
  // The first round neither held nor booked this cycle — a cancelled one is free again.
  const nextRound =
    IN_PERSON_ROUNDS.find(
      (round) => !meetings.some((meeting) => meeting.kind === round.value && meeting.status !== 'cancelled')
    )?.value ?? 'in_person_3';
  const step: Nullable<IDdStep> =
    interviews.data && outreach.data && cycle !== null
      ? ddStep({
          status: current?.status ?? 'none',
          outreach: cadence.length,
          scheduledAt: current?.scheduledAt,
          cleared: Boolean(current?.disclosuresClearedAt),
          u4: disclosures ? u4FlagOf(disclosures) : null,
          nextMeeting: nextMeeting ? { kind: meetingKindOf(nextMeeting), at: nextMeeting.scheduledAt! } : null
        })
      : null;

  const changed = () => {
    void interviews.reload();
    void outreach.reload();
    onChanged();
  };

  const start = async () => {
    setBusy(true);
    setSaid(null);
    try {
      const opened = await ddApi.open(participantId, surveyCode);
      void interviews.reload();
      setOpen(opened.body.interviewId);
    } catch (error) {
      if (error instanceof ApiError && error.isProblem(ProblemType.ddInterviewExists)) {
        const id = error.problem?.interviewId;
        void interviews.reload();
        if (typeof id === 'string') setOpen(id);
      } else if (error instanceof ApiError && error.isProblem(ProblemType.noLiveResponse)) {
        setSaid('There is no response to this survey, so no cycle to hold an interview in.');
      } else {
        setSaid(refusalMessage(error, 'The interview could not be started.'));
      }
    } finally {
      setBusy(false);
    }
  };

  /** One attempt recorded; true when it landed. */
  const send = async (body: Omit<IOutreachBody, 'survey'>): Promise<boolean> => {
    setBusy(true);
    setSaid(null);
    try {
      await ddApi.recordOutreach(participantId, { survey: surveyCode, ...body });
      changed();
      return true;
    } catch (error) {
      setSaid(refusalMessage(error, 'The outreach could not be recorded.'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  // The U4 check is the numbers' own press, drawn where its host put it — above the block in the
  // DD calls panel, in the readiness part on the record page: the step takes the reader to it.
  const toU4 = () => {
    const block = blockRef.current;
    const press =
      block?.querySelector<HTMLButtonElement>('[data-u4-check] button') ??
      block?.closest('[data-dd-panel]')?.querySelector<HTMLButtonElement>('[data-u4-check] button');
    press?.scrollIntoView({ block: 'center' });
    press?.focus();
  };

  const act = (action: IDdStep['action']) => {
    switch (action) {
      case 'log_email':
        return void send({ purpose: 'dd', channel: 'email' });
      case 'log_call':
        return void send({ purpose: 'dd', channel: 'phone' });
      case 'run_u4':
        return toU4();
      case 'confirm_clear':
        return setConfirming(true);
      case 'open_call':
        return current ? setOpen(current.interviewId) : void start();
      case 'open_meeting':
        return nextMeeting && setOpen(nextMeeting.interviewId);
      case 'schedule_meeting':
        return setScheduling(nextRound);
    }
  };

  // Why the step's press is off: the account's roles (said on the screen), or an item open in
  // compliance, which *Confirm clear for DD* waits on (its tooltip).
  const openItems = disclosures?.openItems ?? 0;
  const reads =
    step?.action === 'open_call' ? current !== null : step?.action === 'open_meeting' || step?.action === 'run_u4';
  const stepOff = step && !writable && !reads ? notPermitted : null;
  const stepWaits = step?.action === 'confirm_clear' && openItems > 0;

  return (
    <>
      <div ref={blockRef} className="grid gap-4 text-[0.8125rem]" data-dd-section={surveyCode}>
        {!writable && <NotPermitted id={notPermitted} what="recording due diligence" />}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2" data-dd-status="">
          {!interviews.data && !interviews.error && <span className="text-muted-foreground">Reading…</span>}
          {interviews.error && (
            <span role="alert">
              {interviews.error}{' '}
              <Button type="button" variant="outline" size="sm" onClick={() => void interviews.reload()}>
                Read again
              </Button>
            </span>
          )}
          {interviews.data &&
            (cycle !== null ? (
              <>
                <span>
                  Cycle <span className="font-medium">{cycle}</span>
                </span>
                <DdStatusChip status={current?.status ?? 'none'} dot />
                {current?.completedAt && (
                  <span className="text-muted-foreground">
                    {ddWhen(current.completedAt)} by {current.completedBy?.email ?? 'someone'}
                  </span>
                )}
              </>
            ) : (
              <span className="text-muted-foreground">No response to this survey, so no cycle to interview in.</span>
            ))}
          <span className="ml-auto flex items-center gap-1">
            {step && (
              <OffReason id={primaryOff} reason={stepWaits ? `Off while ${countOf(openItems, 'item is', 'items are')} open in compliance` : null}>
                <Button
                  type="button"
                  size="sm"
                  data-dd-step={step.action}
                  disabled={busy || stepOff !== null || stepWaits}
                  aria-describedby={stepOff ?? (stepWaits ? primaryOff : undefined)}
                  onClick={() => act(step.action)}
                >
                  {step.label}
                </Button>
              </OffReason>
            )}
            {interviews.data && cycle !== null && exception && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="ghost" size="icon" className="size-8" aria-label="More due diligence actions">
                    <Ellipsis className="size-4" aria-hidden />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem disabled={!writable || reasons.length === 0} onSelect={() => setMarking(true)}>
                    Mark as exception…
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <HelpPopover label="About due diligence" align="end">
              <p>
                Three DD emails, then a call attempt. The button names the next step; a booked
                meeting stops the emails.
              </p>
              <p>
                The DD call is the interview of the cycle of the latest response. An exception —
                unresponsive, not an FA, rude — is the Quality rating’s reason, which takes the
                advisor off the DD list.
              </p>
            </HelpPopover>
          </span>
        </div>
        {said && <p role="alert">{said}</p>}
        {told && <p role="status">{told}</p>}

        <Contact
          attempts={outreach.data}
          error={outreach.error}
          again={() => void outreach.reload()}
          cycle={cycle}
          current={current}
          booked={booked}
          writable={writable}
          notPermitted={notPermitted}
          busy={busy}
          heading={Heading}
          send={send}
          onChanged={changed}
        />

        {disclosures && (
          <DisclosuresLine
            participantId={participantId}
            surveyCode={surveyCode}
            facts={disclosures}
            current={current}
            // A clearance needs this cycle's interview, open or opened by it.
            offered={callOpen}
            writable={writable}
            notPermitted={notPermitted}
            checkAbove={checkAbove}
            heading={Heading}
            confirming={confirming}
            onConfirming={setConfirming}
            onMoved={setSaid}
            onChanged={changed}
          />
        )}

        {interviews.data && cycle !== null && (
          <MeetingsTable
            participantId={participantId}
            surveyCode={surveyCode}
            current={current}
            meetings={meetings}
            writable={writable}
            notPermitted={notPermitted}
            busy={busy}
            heading={Heading}
            onStart={() => void start()}
            onOpen={setOpen}
            onSchedule={() => setScheduling(callOpen && !booked ? 'dd_call' : nextRound)}
            onSaid={setSaid}
            onChanged={changed}
          />
        )}

        {earlier.length > 0 && (
          <div className="grid gap-1">
            <Heading className="font-medium">Past cycles</Heading>
            <table className="w-full text-left" aria-label="Earlier interviews">
              <tbody>
                {earlier.map((interview) => (
                  <tr key={interview.interviewId} className="border-t border-border">
                    <td className="py-1 pr-3 tabular-nums">{interview.cycle}</td>
                    <td className="py-1 pr-3">{meetingLabel(meetingKindOf(interview))}</td>
                    <td className="py-1 pr-3">
                      <DdStatusChip status={interview.status} />
                    </td>
                    <td className="py-1 pr-3">
                      {interview.qualityScore && `Score ${qualityLabel(schema, interview.qualityScore)}`}
                    </td>
                    <td className="py-1 text-right">
                      <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={() => setOpen(interview.interviewId)}>
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {scheduling !== null && (
        <ScheduleDialog
          participantId={participantId}
          surveyCode={surveyCode}
          // The DD call is offered while it is still to make; every other kind always.
          kinds={callOpen ? DD_MEETING_KINDS : OTHER_MEETING_KINDS}
          kind={scheduling}
          call={current && callOpen ? bookingOf(current) : null}
          onClose={() => setScheduling(null)}
          onDone={() => {
            setScheduling(null);
            changed();
          }}
          onChanged={changed}
        />
      )}
      {marking && exception && (
        <ExceptionDialog
          name={name}
          reasons={reasons}
          write={exception}
          onClose={() => setMarking(false)}
          onMarked={(sentence) => {
            setMarking(false);
            setTold(sentence);
            changed();
          }}
        />
      )}
      {open !== null && (
        <InterviewSheet
          interviewId={open}
          kindHint={list.find((interview) => interview.interviewId === open)?.kind}
          schema={schema}
          name={name}
          firm={firm}
          writable={writable}
          openItems={disclosures?.openItems ?? null}
          u4={disclosures ? u4FlagOf(disclosures) : null}
          exception={exception}
          onExcepted={(sentence) => {
            setTold(sentence);
            changed();
          }}
          onClose={() => setOpen(null)}
          onChanged={changed}
          onCompleted={onCompleted}
        />
      )}
    </>
  );
}

/** An interview's booking as the schedule dialog opens on it. */
function bookingOf(interview: IDdInterview): IDdBooking {
  return { at: interview.scheduledAt, interviewer: interview.interviewer, note: interview.scheduleNote ?? null };
}

/** A booking's date, Overdue once passed with the meeting still to hold. */
function BookedAt({ interview }: { interview: IDdInterview }) {
  if (interview.status === 'completed' && interview.completedAt) {
    return <span className="text-muted-foreground">{ddWhen(interview.completedAt)}</span>;
  }
  if (!interview.scheduledAt) return <span className="text-muted-foreground">Not scheduled</span>;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span className="tabular-nums">{ddWhen(interview.scheduledAt)}</span>
      {callOverdue(interview.scheduledAt, interview.status) && <DdOverdueChip />}
    </span>
  );
}

/**
 * Every meeting of the cycle in one table (SR-525, C1): the DD call first — a row before it is
 * started, which *Open* starts — then the follow-up calls and the in-person rounds, newest first as
 * survey-api lists them; each its kind, status, date, interviewer and *Open* into its sheet. Under
 * it, *Schedule…* and *Log a meeting* — one held already, its sheet opened at once. Only the DD call
 * moves the DD list, the Quality rating and the DD call flag — survey-api's rule.
 */
function MeetingsTable({
  participantId,
  surveyCode,
  current,
  meetings,
  writable,
  notPermitted,
  busy: blockBusy,
  heading: Heading,
  onStart,
  onOpen,
  onSchedule,
  onSaid,
  onChanged
}: {
  participantId: string;
  surveyCode: string;
  current: Nullable<IDdInterview>;
  meetings: IDdInterview[];
  writable: boolean;
  notPermitted: string;
  busy: boolean;
  heading: 'h3' | 'h5';
  onStart: () => void;
  onOpen: (interviewId: string) => void;
  onSchedule: () => void;
  onSaid: (sentence: string) => void;
  onChanged: () => void;
}) {
  const [logging, setLogging] = React.useState(false);
  const busy = blockBusy || logging;

  const log = async (kind: DdMeetingKind) => {
    setLogging(true);
    try {
      const opened = await ddApi.openMeeting(participantId, { survey: surveyCode, kind });
      onChanged();
      onOpen(opened.body.interviewId);
    } catch (error) {
      onSaid(noCycleRefusal(error) ?? refusalMessage(error, 'The meeting could not be logged.'));
    } finally {
      setLogging(false);
    }
  };

  const row = (interview: IDdInterview) => (
    <tr key={interview.interviewId} className="border-t border-border align-top" data-dd-meeting={meetingKindOf(interview)}>
      <td className="py-1.5 pr-3">
        <span className="font-medium">{meetingLabel(meetingKindOf(interview))}</span>
        {interview.scheduleNote && interview.status !== 'completed' && (
          <span className="block whitespace-pre-wrap text-muted-foreground">{interview.scheduleNote}</span>
        )}
      </td>
      <td className="py-1.5 pr-3">
        <DdStatusChip status={interview.status} />
      </td>
      <td className="py-1.5 pr-3">
        <BookedAt interview={interview} />
      </td>
      <td className="py-1.5 pr-3 text-muted-foreground">{interview.interviewer?.email ?? 'No interviewer'}</td>
      <td className="py-1.5 text-right">
        <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={() => onOpen(interview.interviewId)}>
          Open
        </Button>
      </td>
    </tr>
  );

  return (
    <div className="grid gap-2" data-dd-meetings="">
      <div className="flex items-center gap-2">
        <Heading className="font-medium">Meetings</Heading>
        <HelpPopover label="About DD meetings">
          <p>
            The DD call, follow-up calls and in-person interviews (Rounds 1 to 3) of the cycle of the
            latest response, each logged on its own template.
          </p>
          <p>
            A scheduled meeting goes to its interviewer’s My tasks, with its optional note — where it
            is held, who arranged it. Only the DD call changes the DD list, the Quality rating and the
            DD call flag.
          </p>
        </HelpPopover>
      </div>
      <table className="w-full text-left" aria-label="Meetings">
        <thead className="sr-only">
          <tr>
            <th scope="col">Meeting</th>
            <th scope="col">Status</th>
            <th scope="col">Date</th>
            <th scope="col">Interviewer</th>
            <th scope="col">Open</th>
          </tr>
        </thead>
        <tbody>
          {current ? (
            row(current)
          ) : (
            <tr className="border-t border-border" data-dd-meeting="dd_call">
              <td className="py-1.5 pr-3 font-medium">DD call</td>
              <td className="py-1.5 pr-3">
                <DdStatusChip status="none" />
              </td>
              <td className="py-1.5 pr-3 text-muted-foreground">Not scheduled</td>
              <td className="py-1.5 pr-3 text-muted-foreground">No interviewer</td>
              <td className="py-1.5 text-right">
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0"
                  disabled={!writable || busy}
                  aria-describedby={writable ? undefined : notPermitted}
                  onClick={onStart}
                >
                  Open
                </Button>
              </td>
            </tr>
          )}
          {meetings.map(row)}
        </tbody>
      </table>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!writable || busy}
          aria-describedby={writable ? undefined : notPermitted}
          onClick={onSchedule}
        >
          Schedule…
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!writable || busy}
              aria-describedby={writable ? undefined : notPermitted}
              // The kit trigger misses the block's button size: matched to its neighbour by hand.
              className="text-[13px] font-medium"
            >
              {logging ? 'Logging…' : 'Log a meeting'}
              <ChevronDown className="size-4" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {OTHER_MEETING_KINDS.map((entry) => (
              <DropdownMenuItem key={entry.value} onSelect={() => void log(entry.value)}>
                {entry.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

/**
 * The contact part (SR-525, C1): the DD call's booking, said, the outreach log, and the
 * cadence's two everyday attempts in one press each — *Log email sent*, *Log call attempt* (A3) —
 * logged as of now; another channel, purpose, time or note in the form under *Other outreach…*.
 */
function Contact({
  attempts: read,
  error,
  again,
  cycle,
  current,
  booked,
  writable,
  notPermitted,
  busy,
  heading: Heading,
  send,
  onChanged
}: {
  attempts: Nullable<IOutreachAttempt[]>;
  error: Nullable<string>;
  again: () => void;
  cycle: Nullable<string>;
  current: Nullable<IDdInterview>;
  /** The call booked on this cycle's open interview — said by *Log email sent* (SR-461). */
  booked: Nullable<string>;
  writable: boolean;
  notPermitted: string;
  busy: boolean;
  heading: 'h3' | 'h5';
  send: (body: Omit<IOutreachBody, 'survey'>) => Promise<boolean>;
  onChanged: () => void;
}) {
  const bookedId = React.useId();
  const { user } = useAuth();
  const [channel, setChannel] = React.useState<OutreachChannel>('email');
  const [purpose, setPurpose] = React.useState<OutreachPurpose>('dd');
  const [at, setAt] = React.useState('');
  const [note, setNote] = React.useState('');
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const attempts = read ?? [];
  const ddEmails = attempts.filter(
    (attempt) => attempt.purpose === 'dd' && attempt.channel === 'email' && cycle !== null && attempt.cycle === cycle
  ).length;
  const off = !writable || busy;
  const describedBy = (more?: Nullable<string>) => [!writable && notPermitted, more].filter(Boolean).join(' ') || undefined;

  const record = async (event: React.FormEvent) => {
    event.preventDefault();
    const landed = await send({ purpose, channel, occurredAt: fromLocalInput(at), note: note.trim() || null });
    if (!landed) return;
    setNote('');
    setAt('');
  };

  const withdraw = async (outreachId: string) => {
    setSaid(null);
    try {
      await ddApi.withdrawOutreach(outreachId);
      onChanged();
    } catch (error) {
      setSaid(refusalMessage(error, 'The outreach could not be withdrawn.'));
    }
  };

  return (
    <div className="grid gap-2" data-dd-contact="">
      <Heading className="font-medium">
        Contact{' '}
        <span className="font-normal text-muted-foreground">
          {ddEmails < DD_EMAILS_BEFORE_CALL
            ? `${ddEmails} of ${DD_EMAILS_BEFORE_CALL} DD emails this cycle`
            : `${ddEmails} DD emails this cycle`}
        </span>
      </Heading>
      {current && booked && (
        <div className="grid gap-0.5" id={bookedId} data-dd-scheduled={booked}>
          <p className="flex flex-wrap items-center gap-x-2">
            {callOverdue(booked, current.status) ? (
              <>
                <span className="font-medium">DD call was due {ddWhen(booked)}</span>
                <DdOverdueChip />
              </>
            ) : (
              <span className="font-medium">DD call booked for {ddWhen(booked)}</span>
            )}
            {current.interviewer && <span>with {current.interviewer.email ?? REMOVED_ACCOUNT}</span>}
            {current.scheduledBy && (
              <span className="text-muted-foreground">
                set by {current.scheduledBy.email ?? REMOVED_ACCOUNT}
                {current.scheduleSetAt ? ` on ${ddWhen(current.scheduleSetAt)}` : ''}
              </span>
            )}
          </p>
          {current.scheduleNote && <p className="whitespace-pre-wrap">{current.scheduleNote}</p>}
        </div>
      )}
      {error && (
        <p role="alert">
          {error}{' '}
          <Button type="button" variant="outline" size="sm" onClick={again}>
            Read again
          </Button>
        </p>
      )}
      {read && attempts.length === 0 && <p className="text-muted-foreground">No outreach recorded.</p>}
      {attempts.length > 0 && (
        <ul className="grid gap-1" aria-label="Outreach">
          {attempts.map((attempt) => (
            <li key={attempt.outreachId} className="flex flex-wrap items-center gap-2" data-outreach={attempt.outreachId}>
              <span className="tabular-nums">{ddWhen(attempt.occurredAt)}</span>
              <span className="font-medium">{outreachWord(OUTREACH_CHANNELS, attempt.channel)}</span>
              <span className="text-muted-foreground">{outreachWord(OUTREACH_PURPOSES, attempt.purpose)}</span>
              {attempt.note && <span>{attempt.note}</span>}
              <span className="text-muted-foreground">{attempt.recordedBy.email}</span>
              {writable && attempt.recordedBy.accountId === user?.accountId && (
                <Button type="button" variant="link" size="sm" onClick={() => void withdraw(attempt.outreachId)}>
                  Withdraw
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={off}
          aria-describedby={describedBy(booked && current ? bookedId : null)}
          onClick={() => void send({ purpose: 'dd', channel: 'email' })}
        >
          Log email sent
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={off}
          aria-describedby={describedBy()}
          onClick={() => void send({ purpose: 'dd', channel: 'phone' })}
        >
          Log call attempt
        </Button>
        <HelpPopover label="About logging outreach">
          <p>
            Log email sent and Log call attempt record a DD attempt as of now. Another channel or
            purpose, an earlier time or a note: Other outreach….
          </p>
          <p>A booked call is said above, so nobody sends the advisor another DD email.</p>
        </HelpPopover>
      </div>
      <details className="grid gap-2" data-outreach-more="">
        <summary className="cursor-pointer text-muted-foreground">Other outreach…</summary>
        <form className="mt-2 flex flex-wrap items-end gap-2" onSubmit={(event) => void record(event)} aria-label="Record outreach">
          <label className="grid gap-1">
            Channel
            <Select value={channel} onValueChange={(value) => setChannel(value as OutreachChannel)} disabled={!writable}>
              <SelectTrigger className="h-8 w-28" aria-label="Channel">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OUTREACH_CHANNELS.map((entry) => (
                  <SelectItem key={entry.value} value={entry.value}>
                    {entry.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="grid gap-1">
            About
            <Select value={purpose} onValueChange={(value) => setPurpose(value as OutreachPurpose)} disabled={!writable}>
              <SelectTrigger className="h-8 w-36" aria-label="About">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OUTREACH_PURPOSES.map((entry) => (
                  <SelectItem key={entry.value} value={entry.value}>
                    {entry.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className="grid gap-1">
            When <span className="sr-only">(blank: now)</span>
            <Input type="datetime-local" className="h-8 w-52" value={at} onChange={(e) => setAt(e.target.value)} disabled={!writable} />
          </label>
          <label className="grid min-w-48 flex-1 gap-1">
            Note
            <Input className="h-8" value={note} maxLength={2000} onChange={(e) => setNote(e.target.value)} disabled={!writable} />
          </label>
          <Button type="submit" size="sm" disabled={off} aria-describedby={describedBy()}>
            {busy ? 'Recording…' : 'Record outreach'}
          </Button>
        </form>
      </details>
      {said && <p role="alert">{said}</p>}
    </div>
  );
}

/** The registries *Ask … again* names on a person's CRD — the numbers' own words. */
const CRD_REGISTRIES = REFRESH_OF_ID_TYPE.get('CRD') ?? 'the registries';

/**
 * The U4 check (SR-498): the flag — issue found, clear, not checked — a link to the record's
 * Disclosures section, when the registries last answered, and *Run U4 check*: the numbers'
 * `RefreshControl` on the CRD (BrokerCheck and IAPD asked again, under `responses.annotate`), so
 * the answer lands as any other lookup's does. Drawn in the DD block's Disclosures line, and in
 * the DD calls panel's header, beside the advisor's name.
 */
export function U4Check({
  participantId,
  facts,
  onChanged
}: {
  participantId: string;
  facts: IDdDisclosures;
  onChanged: () => void;
}) {
  const canAsk = useCan()('responses.annotate');
  const askReason = React.useId();
  const [note, setNote] = React.useState<Nullable<INumberNote>>(null);
  const [asked, setAsked] = React.useState(false);
  // No CRD, nothing to check: no flag and no press (a team or a firm, until SR-467).
  if (u4FlagOf(facts) === 'no_crd') return null;
  return (
    <div className="grid gap-1 text-[0.8125rem]" data-u4-check="">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <U4FlagLink facts={facts} />
        {!facts.noCrd && (
          <span className="text-muted-foreground" data-dd-checked={facts.checkedAt ?? ''}>
            {facts.checkedAt ? `checked ${ddWhen(facts.checkedAt)}` : facts.checked ? 'checked' : 'never checked'}
          </span>
        )}
        {facts.crd && (
          <RefreshControl
            participantId={participantId}
            identifier={{ idType: 'CRD', value: facts.crd }}
            registry={CRD_REGISTRIES}
            label="Run U4 check"
            held={facts.waiting || asked}
            notPermitted={canAsk ? undefined : askReason}
            say={setNote}
            onAsked={async () => {
              setAsked(true);
              onChanged();
              return true;
            }}
          />
        )}
        {asked && !note && <span className="text-muted-foreground">Asked {CRD_REGISTRIES}; the answer lands shortly.</span>}
      </p>
      {facts.crd && !canAsk && <NotPermitted id={askReason} what="running a U4 check" />}
      {note && <p role={note.tone === 'refused' ? 'alert' : 'status'}>{[note.title, note.text].filter(Boolean).join(': ')}</p>}
    </div>
  );
}

/**
 * Ready for the call (SR-460; SR-525, C1): the U4 check (`U4Check`) — unless the host draws it
 * above, as the DD calls panel does — and *Confirm clear for DD*, which records the check on this
 * cycle's interview. Confirm is off while an item is open in compliance — survey-api's own 409 —
 * the count in its tooltip, and once confirmed the part says by whom and when. The dialog is the
 * block's to open too: it is the step's press while the call is booked and not cleared.
 */
function DisclosuresLine({
  participantId,
  surveyCode,
  facts,
  current,
  offered,
  writable,
  notPermitted,
  checkAbove,
  heading: Heading,
  confirming,
  onConfirming,
  onMoved,
  onChanged
}: {
  participantId: string;
  surveyCode: string;
  facts: IDdDisclosures;
  /** This cycle's interview, which a clearance is recorded on — null before one is opened. */
  current: Nullable<IDdInterview>;
  /** *Confirm clear for DD* is offered: a cycle, and its call not completed. */
  offered: boolean;
  writable: boolean;
  notPermitted: string;
  /** The host draws the U4 check itself, above the block. */
  checkAbove: boolean;
  heading: 'h3' | 'h5';
  confirming: boolean;
  onConfirming: (open: boolean) => void;
  /** A refusal saying the block moved — a completed call, no cycle — for the block's own alert. */
  onMoved: (sentence: string) => void;
  onChanged: () => void;
}) {
  const openReason = React.useId();
  const open = facts.openItems ?? 0;
  const cleared = current?.disclosuresClearedAt ? current : null;
  const confirmable = offered && !cleared;

  const confirm = async (text: string): Promise<Nullable<string>> => {
    try {
      await ddApi.clearDisclosures(participantId, surveyCode, text.trim() || null);
      onChanged();
      return null;
    } catch (error) {
      if (error instanceof ApiError) {
        const count = error.problem?.count;
        const reason = error.problem?.reason;
        const open = error.isProblem(ProblemType.openComplianceItems);
        const sentence = open
          ? `${typeof count === 'number' ? count : 'Some'} open in compliance — nothing was confirmed.`
          : error.isProblem(ProblemType.noLiveResponse)
            ? 'There is no response to this survey, so no cycle to confirm the disclosures in.'
            : error.isProblem(ProblemType.invalidTransition)
              ? typeof reason === 'string'
                ? reason
                : 'The DD call is completed.'
              : null;
        if (sentence !== null) {
          // An open item is the dialog's to say; a block moved since, the block's.
          if (!open) onMoved(sentence);
          onChanged();
          return open ? sentence : null;
        }
      }
      return refusalMessage(error, 'The disclosures could not be confirmed clear.');
    }
  };

  if (checkAbove && !cleared && !confirmable) return null;
  return (
    <div className="grid gap-1" data-dd-disclosures="">
      <Heading className="font-medium">Ready for the call</Heading>
      {!checkAbove && <U4Check participantId={participantId} facts={facts} onChanged={onChanged} />}
      {cleared && (
        <p data-dd-cleared={cleared.disclosuresClearedAt}>
          Cleared for DD by {cleared.disclosuresClearedBy?.email ?? REMOVED_ACCOUNT} on{' '}
          {ddWhen(cleared.disclosuresClearedAt ?? null)}
          {cleared.disclosuresClearNote && <span className="text-muted-foreground"> — {cleared.disclosuresClearNote}</span>}
        </p>
      )}
      {confirmable && (
        <div className="flex flex-wrap items-center gap-2">
          <OffReason
            id={openReason}
            reason={writable && open > 0 ? `Off while ${countOf(open, 'item is', 'items are')} open in compliance` : null}
          >
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!writable || open > 0}
              aria-describedby={!writable ? notPermitted : open > 0 ? openReason : undefined}
              onClick={() => onConfirming(true)}
            >
              Confirm clear for DD
            </Button>
          </OffReason>
        </div>
      )}
      <ReasonDialog
        open={confirming}
        onOpenChange={onConfirming}
        title="Confirm clear for DD"
        description="The disclosures are recorded as checked and clear on this cycle’s DD interview."
        reasonLabel="Note"
        required={false}
        max={2000}
        confirmLabel="Confirm clear"
        onConfirm={confirm}
      />
    </div>
  );
}

type Draft = { answers: Record<string, string>; score: string };

/** How long typing rests before the draft saves itself (D4). */
const AUTOSAVE_MS = 1500;

/** The revision to quote: the `ETag`, else the body's own — survey-api sends both alike. */
function heldOf({ body, etag }: IEtagged<IDdInterview>): { interview: IDdInterview; etag: string } {
  return { interview: body, etag: etag ?? `"${body.revision}"` };
}

function draftOf(interview: IDdInterview): Draft {
  return { answers: { ...interview.answers }, score: interview.qualityScore ?? '' };
}

/** The answers on screen hold nothing the interview does not: a blank and an absent are one. */
function sameAnswers(typed: Record<string, string>, held: Record<string, string>): boolean {
  const codes = new Set([...Object.keys(typed), ...Object.keys(held)]);
  return [...codes].every((code) => (typed[code] ?? '') === (held[code] ?? ''));
}

/** How the sheet begins (D1): the choice of the two, the paste, or the sections typed into. */
type Entry = 'choose' | 'paste' | 'type';

/**
 * One interview — the DD call or another meeting (SR-501) — in a sheet beside its host, or
 * `inline` on a page of its own (My tasks' meeting task). SR-525 lays it out for the call itself:
 *
 * - **Context first** (D3): the firm, the cycle, the booking, the U4 flag or the clearance, and the
 *   template's version in the header; the booking under it, moved only with *Reschedule…* (A2).
 * - **Paste from Granola or type by hand** (D1) is the first thing an empty interview asks; after
 *   *Apply*, the sections the notes left empty fold under one line. An ingested interview (SR-276's
 *   Granola import, still to come) will open on its review here, where the choice stands.
 * - **The draft saves itself** (D4) ~1.5 s after typing stops, under the revision held, and quietly:
 *   the host is not re-read for it. A conflict stops it and is said as any save's is.
 * - **The footer stays in view** (D2): the Quality score, *Save draft*, *Complete*, and — on the DD
 *   call — *Couldn't complete…*, the exception (D5); a meeting's *Cancel meeting…* instead.
 *
 * A meeting other than the DD call takes no disclosures check and an optional score, kept on the
 * meeting alone; it can be cancelled while not completed and switched to another kind while it
 * holds no answers.
 */
export function InterviewSheet({
  interviewId,
  kindHint,
  schema,
  name,
  firm = null,
  writable,
  openItems,
  u4 = null,
  exception,
  inline = false,
  initial,
  onClose,
  onChanged,
  onCompleted,
  onExcepted
}: {
  interviewId: string;
  /** The kind the host already knows, for the title before the read lands. */
  kindHint?: DdMeetingKind;
  schema: Nullable<IInternalFormSchema>;
  name: string;
  /** The advisor's firm, for the context strip. */
  firm?: Nullable<string>;
  writable: boolean;
  /** The participant's items open in compliance, as the block's host read them (SR-460). */
  openItems: Nullable<number>;
  /** The U4 flag as the host read it, for the context strip; null draws none. */
  u4?: Nullable<U4Flag>;
  /** The host's exception write (`dd-exception.tsx`): *Couldn't complete…* on the DD call. */
  exception?: DdExceptionWrite;
  /** Drawn on the page, not in a sheet: nothing to close. */
  inline?: boolean;
  /** The interview as the host already read it — the first state, not read again. */
  initial?: IEtagged<IDdInterview>;
  onClose?: () => void;
  /** Told after every write but the draft's own saves, with the interview as it landed. */
  onChanged: (interview: IDdInterview) => void;
  /** Told when the DD call is completed here — never for another meeting. */
  onCompleted?: (interview: IDdInterview, etag: string) => void;
  /** Told once an exception landed from here, with what to say. */
  onExcepted?: (sentence: string) => void;
}) {
  const [attempt, setAttempt] = React.useState(0);
  const [held, setHeld] = React.useState<Nullable<{ interview: IDdInterview; etag: string }>>(() =>
    initial ? heldOf(initial) : null
  );
  const [draft, setDraft] = React.useState<Nullable<Draft>>(() => (initial ? draftOf(initial.body) : null));
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const [told, setTold] = React.useState<Nullable<string>>(null);
  const [busy, setBusy] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [savedAt, setSavedAt] = React.useState<Nullable<number>>(null);
  const [conflicted, setConflicted] = React.useState(false);
  const [entry, setEntry] = React.useState<Nullable<Entry>>(null);
  const [folded, setFolded] = React.useState<ReadonlySet<string>>(new Set());
  const [reopening, setReopening] = React.useState(false);
  const [cancelling, setCancelling] = React.useState(false);
  const [unclear, setUnclear] = React.useState(false);
  const [rescheduling, setRescheduling] = React.useState(false);
  const [marking, setMarking] = React.useState(false);
  const kindId = React.useId();
  const scoreId = React.useId();
  const scores = schema ? scoreOptions(schema) : null;
  // The latest of each, for a save begun by a timer or a dialog after the render that set it.
  const heldRef = React.useRef(held);
  heldRef.current = held;
  const draftRef = React.useRef(draft);
  draftRef.current = draft;
  // The write still out: the next waits for it, so a press or a close that overtakes an autosave
  // quotes the revision that autosave moved rather than the one it was sent under (a false
  // conflict). Never rejects.
  const flight = React.useRef<Promise<unknown>>(Promise.resolve());
  // The draft an autosave was refused on: not sent again until typing moves it — a refusal that
  // stands (a permission taken away, a section over its bound) is not re-sent every 1.5 s.
  const stalled = React.useRef<Nullable<Draft>>(null);

  React.useEffect(() => {
    if (attempt === 0 && initial?.body.interviewId === interviewId) return;
    let live = true;
    ddApi.read(interviewId).then(
      (answer) => {
        if (!live) return;
        setHeld(heldOf(answer));
        setDraft(draftOf(answer.body));
        setConflicted(false);
      },
      (error: unknown) => live && setSaid(refusalMessage(error, 'The interview could not be read.'))
    );
    return () => {
      live = false;
    };
  }, [interviewId, attempt]);

  const interview = held?.interview ?? null;
  const kind = interview ? meetingKindOf(interview) : (kindHint ?? 'dd_call');
  const meeting = kind !== 'dd_call';
  const done = interview?.status === 'completed';
  const cancelled = interview?.status === 'cancelled';
  const editable = writable && !done && !cancelled;
  // survey-api switches a meeting's kind only while it holds no answers; text typed and not yet
  // saved holds the switch too, as the new kind's template may lack its sections.
  const switchable =
    meeting && editable && interview !== null && Object.values(interview.answers).every((value) => !value);
  const typed = Object.values(draft?.answers ?? {}).some(Boolean);
  const dirty = interview !== null && draft !== null && !sameAnswers(draft.answers, interview.answers);
  // An empty interview asks how its notes come in; one with anything in it opens on its sections.
  const blank = interview !== null && !typed && !interview.unmatchedText && interview.source !== 'ingested';
  const shown: Entry = entry ?? (editable && blank ? 'choose' : 'type');
  const reasons = schema ? reasonOptions(schema) : [];

  /** One write under the revision held: null when it landed, else what refused it — said in the
   *  sheet unless `silent`. `keep` builds the next draft from the one on screen and the answer;
   *  `quiet` is the draft's own save, which tells the host nothing. */
  const write = async (
    send: (etag: string) => Promise<IEtagged<IDdInterview>>,
    failed: string,
    { keep, quiet = false, silent = false }: { keep?: (draft: Draft, answer: IDdInterview) => Draft; quiet?: boolean; silent?: boolean } = {}
  ): Promise<Nullable<string>> => {
    if (!heldRef.current || !draftRef.current) return 'The interview is not read yet.';
    if (quiet) setSaving(true);
    else setBusy(true);
    if (!quiet) setSaid(null);
    const prior = flight.current;
    let release = () => {};
    flight.current = new Promise<void>((resolve) => (release = resolve));
    await prior;
    const was = heldRef.current!;
    try {
      const answer = await send(was.etag);
      const next = heldOf(answer);
      heldRef.current = next;
      setHeld(next);
      if (
        next.interview.status === 'completed' &&
        was.interview.status !== 'completed' &&
        meetingKindOf(next.interview) === 'dd_call'
      ) {
        onCompleted?.(next.interview, next.etag);
      }
      setDraft((current) => (keep && current ? keep(current, answer.body) : draftOf(answer.body)));
      setSavedAt(Date.now());
      if (!quiet) onChanged(next.interview);
      return null;
    } catch (error) {
      let refusal: string;
      if (error instanceof ApiError && error.isProblem(ProblemType.revisionConflict)) {
        setConflicted(true);
        refusal = 'Someone saved this interview since you opened it. Read it again to see theirs; your text stays until you do.';
        setSaid(refusal);
      } else {
        refusal = refusalMessage(error, failed);
        if (!silent) setSaid(refusal);
      }
      return refusal;
    } finally {
      release();
      if (quiet) setSaving(false);
      else setBusy(false);
    }
  };

  /** A transition's answer taken as the interview and the draft. */
  const adopt = (answer: IEtagged<IDdInterview>) => {
    setHeld(heldOf(answer));
    setDraft(draftOf(answer.body));
    onChanged(answer.body);
  };

  // The booking travels back as held: survey-api stores what a save sends, and the sheet moves it
  // only through *Reschedule…*.
  const body = () => ({
    answers: draftRef.current?.answers ?? {},
    scheduledAt: heldRef.current?.interview.scheduledAt ?? null,
    interviewerAccountId: heldRef.current?.interview.interviewer?.accountId ?? null
  });

  // D4: what was typed saves itself once typing rests, never over a conflict or another write.
  const autosave = async () => {
    const sent = draftRef.current;
    const refusal = await write((etag) => ddApi.save(interviewId, etag, body()), 'The draft could not be saved.', {
      keep: (current) => current,
      quiet: true
    });
    if (refusal !== null) stalled.current = sent;
    return refusal;
  };
  React.useEffect(() => {
    if (!editable || !dirty || busy || saving || conflicted || draft === stalled.current) return;
    const timer = window.setTimeout(() => void autosave(), AUTOSAVE_MS);
    return () => window.clearTimeout(timer);
  }, [draft, editable, dirty, busy, saving, conflicted]);

  // What is typed and not yet saved, read after the write still out has landed.
  const unsaved = () => {
    const typedNow = draftRef.current;
    const savedNow = heldRef.current;
    return typedNow !== null && savedNow !== null && !sameAnswers(typedNow.answers, savedNow.interview.answers);
  };

  // Closing waits for what is typed to be saved; a refusal keeps the sheet open, said.
  const close = async () => {
    if (editable && !conflicted && !busy) {
      await flight.current;
      if (unsaved() && (await autosave()) !== null) return;
    }
    onClose?.();
  };

  // Leaving the page with the timer still running (My tasks' page, drawn inline) saves what it
  // would have — under the latest revision, after any write still out.
  const leave = React.useRef<() => void>(() => {});
  leave.current = () => {
    if (editable && !conflicted && draftRef.current !== stalled.current) {
      void flight.current.then(() => unsaved() && void autosave());
    }
  };
  React.useEffect(() => () => leave.current(), []);

  // What *Complete the DD call* asks over: no clearance this cycle, items open in compliance, or both.
  const notCleared = !interview?.disclosuresClearedAt;
  const unclearTitle =
    (notCleared && openItems
      ? `Not confirmed clear, ${openItems} open in compliance`
      : notCleared
        ? 'Disclosures are not confirmed clear'
        : `${openItems ?? 0} open in compliance`) + ' — complete anyway?';

  const complete = () => {
    const score = draft?.score ?? '';
    return write(
      (etag) =>
        ddApi.complete(interviewId, etag, {
          ...body(),
          // A meeting's score is optional and sent only when chosen.
          ...(meeting && !score ? {} : { qualityScore: score })
        }),
      meeting ? 'The meeting could not be completed.' : 'The DD call could not be completed.'
    );
  };

  // A switch writes the booking as held, and keeps on screen only the answers the new kind's
  // template has a section for.
  const switchKind = (next: string) =>
    write(
      (etag) =>
        ddApi.save(interviewId, etag, {
          kind: next as DdMeetingKind,
          scheduledAt: interview?.scheduledAt ?? null,
          interviewerAccountId: interview?.interviewer?.accountId ?? null
        }),
      'The meeting could not be switched.',
      {
        keep: (was, answer) => ({
          ...was,
          answers: Object.fromEntries(
            Object.entries(was.answers).filter(([code]) => answer.template.sections.some((section) => section.code === code))
          )
        })
      }
    );

  // The DD call's booking moves through its own `PUT`, which tells the sheet nothing: the
  // interview is read again under the draft on screen.
  const reread = async () => {
    try {
      const answer = await ddApi.read(interviewId);
      setHeld(heldOf(answer));
      onChanged(answer.body);
    } catch (error) {
      setSaid(refusalMessage(error, 'The interview could not be read.'));
    }
  };
  const rebooked = () => {
    setRescheduling(false);
    void reread();
  };

  const cancelledEvent = cancelled
    ? [...(interview?.history ?? [])].reverse().find((event) => event.kind === 'cancelled')
    : undefined;
  const title = meeting ? `${meetingLabel(kind)} · ${name}` : `DD interview with ${name}`;
  const Title: React.ElementType = inline ? 'h2' : SheetTitle;
  const Description: React.ElementType = inline ? 'p' : SheetDescription;
  const empties = interview?.template.sections.filter((section) => folded.has(section.code)) ?? [];

  const sectionField = (section: IDdSection) =>
    draft && (
      <label key={section.code} className="grid gap-1" data-dd-answer={section.code}>
        <span>
          {section.label}
          {section.required && <span aria-hidden> *</span>}
        </span>
        {section.kind === 'bool' ? (
          <Select
            value={draft.answers[section.code] ?? ''}
            onValueChange={(value) => setDraft({ ...draft, answers: { ...draft.answers, [section.code]: value } })}
            disabled={!editable}
          >
            <SelectTrigger className="h-8 w-28" aria-label={section.label}>
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="yes">Yes</SelectItem>
              <SelectItem value="no">No</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <AutosizeTextarea
            minRows={section.kind === 'text' ? 1 : 3}
            value={draft.answers[section.code] ?? ''}
            readOnly={!editable}
            onChange={(event) => setDraft({ ...draft, answers: { ...draft.answers, [section.code]: event.target.value } })}
          />
        )}
      </label>
    );

  const content = (
    <>
      <div className="grid gap-1 border-b border-border px-5 py-4">
        <Title className={`text-[0.9375rem] text-[var(--color-royal-blue)]${inline ? ' font-semibold' : ''}`}>{title}</Title>
        {/* The kit hides a sheet's description; the context strip is read on the screen (D3). */}
        <Description className={`not-sr-only text-[0.8125rem]${inline ? ' text-muted-foreground' : ''}`}>
          {interview ? (
            <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1" data-dd-context="">
              {firm && <span>{firm} ·</span>}
              <span>Cycle {interview.cycle} ·</span>
              {interview.scheduledAt && !done && <span>booked for {ddWhen(interview.scheduledAt)} ·</span>}
              {!meeting &&
                (interview.disclosuresClearedAt ? (
                  <StatusChip tone="success">Cleared for DD</StatusChip>
                ) : (
                  u4 &&
                  u4 !== 'no_crd' && <StatusChip tone={U4_FLAGS[u4].tone}>{U4_FLAGS[u4].label}</StatusChip>
                ))}
              <span>
                template v{interview.template.version}
                {interview.template.isDefault ? ' (stand-in outline)' : ''} ·
              </span>
              <DdStatusChip status={interview.status} />
            </span>
          ) : (
            'Reading…'
          )}
        </Description>
      </div>
      {interview && draft && (
        <form
          className="flex flex-1 flex-col gap-4 px-5 pt-4 text-[0.8125rem]"
          data-dd-notes-source={interview.source}
          onSubmit={(event) => {
            event.preventDefault();
            void write((etag) => ddApi.save(interviewId, etag, body()), 'The interview could not be saved.');
          }}
        >
          {said && (
            <Alert>
              <AlertDescription role="alert">
                {said}{' '}
                <Button type="button" variant="outline" size="sm" onClick={() => setAttempt((count) => count + 1)}>
                  Read again
                </Button>
              </AlertDescription>
            </Alert>
          )}
          {told && <p role="status">{told}</p>}
          {cancelled && (
            <p data-dd-cancelled="">
              Cancelled
              {cancelledEvent && ` ${ddWhen(cancelledEvent.at)} by ${cancelledEvent.actor.email ?? REMOVED_ACCOUNT}`}
              {cancelledEvent?.detail && <span className="text-muted-foreground"> — {cancelledEvent.detail}</span>}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-dd-booking="">
            {interview.scheduledAt ? (
              <span>
                {meeting ? 'Booked for' : 'DD call booked for'}{' '}
                <span className="font-medium">{ddWhen(interview.scheduledAt)}</span>
              </span>
            ) : (
              <span className="text-muted-foreground">Not scheduled</span>
            )}
            <span className="text-muted-foreground">
              {interview.interviewer ? `Interviewer ${interview.interviewer.email ?? REMOVED_ACCOUNT}` : 'No interviewer'}
            </span>
            {editable && (
              <Button type="button" variant="link" size="sm" className="h-auto p-0" disabled={busy} onClick={() => setRescheduling(true)}>
                {interview.scheduledAt ? 'Reschedule…' : 'Schedule…'}
              </Button>
            )}
          </div>
          {interview.scheduleNote && <p className="whitespace-pre-wrap">Note: {interview.scheduleNote}</p>}
          {switchable && (
            <div className="grid gap-1">
              <span id={kindId}>Meeting</span>
              <ChoiceSelect
                labelledBy={kindId}
                className="h-8 w-80"
                value={kind}
                onChange={(next) => next !== kind && void switchKind(next)}
                choices={OTHER_MEETING_KINDS}
                disabled={busy || typed}
              />
            </div>
          )}
          {shown === 'choose' && (
            <div className="flex flex-wrap items-center gap-2 rounded border border-dashed border-border p-4" data-dd-entry="">
              <Button key="paste" type="button" size="sm" onClick={() => setEntry('paste')}>
                Paste from Granola
              </Button>
              <Button key="type" type="button" variant="outline" size="sm" onClick={() => setEntry('type')}>
                Type by hand
              </Button>
              <HelpPopover label="About pasting notes">
                <p>
                  Granola’s notes are split by their headings into the template’s sections — a
                  preview first, written only on Apply. Sections the notes leave empty fold away.
                </p>
              </HelpPopover>
            </div>
          )}
          {editable && shown !== 'choose' && (
            <PasteNotes
              key={shown}
              interviewId={interviewId}
              sections={interview.template.sections}
              busy={busy || saving}
              open={shown === 'paste'}
              onTypeInstead={() => setEntry('type')}
              onApply={async (text, filled) => {
                const refusal = await write(
                  (etag) => ddApi.importNotes(interviewId, etag, text),
                  'The notes could not be applied.',
                  {
                    keep: (was, answer) => ({
                      ...was,
                      answers: {
                        ...was.answers,
                        ...Object.fromEntries(filled.map((code) => [code, answer.answers[code] ?? '']))
                      }
                    })
                  }
                );
                if (refusal !== null) return false;
                // The sections the notes left empty fold under one line, typed into from there.
                const answers = { ...draftRef.current?.answers, ...Object.fromEntries(filled.map((code) => [code, 'x'])) };
                setFolded(new Set(interview.template.sections.filter((section) => !answers[section.code]).map((section) => section.code)));
                setEntry('type');
                return true;
              }}
            />
          )}
          {shown === 'type' && (
            <>
              {interview.template.sections.filter((section) => !folded.has(section.code)).map(sectionField)}
              {empties.length > 0 && (
                <details className="grid gap-4" data-dd-empty-sections="">
                  <summary className="cursor-pointer text-muted-foreground">
                    {countOf(empties.length, 'section', 'sections')} the notes left empty
                  </summary>
                  <div className="mt-3 grid gap-4">{empties.map(sectionField)}</div>
                </details>
              )}
            </>
          )}
          {interview.unmatchedText && (
            <div className="grid gap-1">
              <span className="font-medium">Notes no section took</span>
              <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded border border-border p-2 text-[0.8125rem]">
                {interview.unmatchedText}
              </pre>
            </div>
          )}
          {done && interview.completedAt && (
            <p className="text-muted-foreground">
              Completed {ddWhen(interview.completedAt)} by {interview.completedBy?.email ?? 'someone'}
              {meeting ? '.' : '; the advisor left the DD list for everyone.'}
            </p>
          )}
          <History interview={interview} />
          <div
            className="sticky bottom-0 z-10 -mx-5 mt-auto flex flex-wrap items-end gap-3 border-t border-border bg-white px-5 py-3"
            data-dd-footer=""
          >
            {meeting ? (
              <div className="grid gap-1">
                <span id={scoreId}>Quality score</span>
                <ChoiceSelect
                  labelledBy={scoreId}
                  className="h-8 w-44"
                  value={draft.score}
                  onChange={(value) => setDraft({ ...draft, score: value })}
                  choices={[{ value: '', label: 'No score' }, ...(scores ?? []).map((option) => ({ value: option.code, label: option.label }))]}
                  disabled={!editable || scores === null}
                />
              </div>
            ) : (
              <label className="grid gap-1">
                Quality score
                <Select
                  value={draft.score}
                  onValueChange={(value) => setDraft({ ...draft, score: value })}
                  disabled={!editable || scores === null}
                >
                  <SelectTrigger className="h-8 w-44" aria-label="Quality score">
                    <SelectValue placeholder="Choose a score" />
                  </SelectTrigger>
                  <SelectContent>
                    {(scores ?? []).map((option) => (
                      <SelectItem key={option.code} value={option.code}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
            )}
            {editable ? (
              <>
                <Button key="save" type="submit" variant="outline" size="sm" disabled={busy || saving}>
                  Save draft
                </Button>
                {meeting ? (
                  <>
                    <Button key="complete" type="button" size="sm" disabled={busy || saving} onClick={() => void complete()}>
                      Complete the meeting
                    </Button>
                    <Button key="cancel" type="button" variant="ghost" size="sm" disabled={busy} onClick={() => setCancelling(true)}>
                      Cancel meeting…
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      key="complete"
                      type="button"
                      size="sm"
                      disabled={busy || saving || draft.score === ''}
                      onClick={() =>
                        // Not confirmed clear, or an item open in compliance: asked first, never refused.
                        interview.disclosuresClearedAt && !openItems ? void complete() : setUnclear(true)
                      }
                    >
                      Complete the DD call
                    </Button>
                    {exception && (
                      <Button
                        key="exception"
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={busy || reasons.length === 0}
                        onClick={() => setMarking(true)}
                      >
                        Couldn’t complete…
                      </Button>
                    )}
                  </>
                )}
                <span className="ml-auto self-center text-muted-foreground" data-dd-draft-state={saving ? 'saving' : dirty ? 'unsaved' : 'saved'}>
                  {saving ? 'Saving…' : dirty ? 'Unsaved changes' : savedAt ? `Saved ${formatClock(savedAt)}` : ''}
                </span>
              </>
            ) : (
              done &&
              writable && (
                <Button key="reopen" type="button" variant="outline" size="sm" onClick={() => setReopening(true)}>
                  Reopen…
                </Button>
              )
            )}
          </div>
        </form>
      )}
      {!interview && said && <p className="px-5 py-4" role="alert">{said}</p>}
      <AlertDialog open={unclear} onOpenChange={setUnclear}>
        <AlertDialogContent data-dd-unclear="">
          <AlertDialogHeader>
            <AlertDialogTitle>{unclearTitle}</AlertDialogTitle>
            <AlertDialogDescription>Complete the DD call as it stands, or cancel to check first.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              type="button"
              onClick={() => {
                setUnclear(false);
                void complete();
              }}
            >
              Complete anyway
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ReasonDialog
        open={reopening}
        onOpenChange={setReopening}
        title={meeting ? 'Reopen the meeting' : 'Reopen the DD call'}
        description={
          meeting
            ? 'The meeting goes back to in progress.'
            : 'The DD call flag is cleared and the advisor goes back on the DD list.'
        }
        required
        confirmLabel="Reopen"
        onConfirm={async (reason) => {
          await flight.current;
          if (!heldRef.current) return 'The interview is not read yet.';
          try {
            adopt(await ddApi.reopen(interviewId, heldRef.current.etag, reason));
            return null;
          } catch (error) {
            return refusalMessage(error, 'The interview could not be reopened.');
          }
        }}
      />
      <ReasonDialog
        open={cancelling}
        onOpenChange={setCancelling}
        title="Cancel the meeting"
        description="The meeting is kept as cancelled and leaves its interviewer’s My tasks."
        reasonLabel="Reason"
        required={false}
        max={2000}
        confirmLabel="Cancel meeting"
        onConfirm={async (reason) => {
          await flight.current;
          if (!heldRef.current) return 'The meeting is not read yet.';
          try {
            adopt(await ddApi.cancel(interviewId, heldRef.current.etag, reason.trim() || null));
            return null;
          } catch (error) {
            return refusalMessage(error, 'The meeting could not be cancelled.');
          }
        }}
      />
      {rescheduling && interview && (
        <ScheduleDialog
          participantId={interview.participantId}
          surveyCode={interview.survey}
          kinds={[{ value: kind, label: meetingLabel(kind) }]}
          kind={kind}
          call={meeting ? null : bookingOf(interview)}
          meeting={
            meeting
              ? {
                  kind,
                  ...bookingOf(interview),
                  // The answers on screen travel with it, as a *Save draft* would send them.
                  save: (scheduledAt, interviewerAccountId, scheduleNote) =>
                    write(
                      (etag) =>
                        ddApi.save(interviewId, etag, { answers: body().answers, scheduledAt, interviewerAccountId, scheduleNote }),
                      'The meeting could not be rescheduled.',
                      { keep: (current) => current, silent: true }
                    )
                }
              : undefined
          }
          onClose={() => setRescheduling(false)}
          onDone={() => (meeting ? setRescheduling(false) : rebooked())}
          onChanged={() => void reread()}
        />
      )}
      {marking && exception && (
        <ExceptionDialog
          name={name}
          reasons={reasons}
          write={exception}
          onClose={() => setMarking(false)}
          onMarked={(sentence) => {
            setMarking(false);
            setTold(sentence);
            onExcepted?.(sentence);
          }}
        />
      )}
    </>
  );

  if (inline) {
    return (
      <section className="rounded border border-border bg-white" data-dd-interview={interviewId} aria-label={title}>
        {content}
      </section>
    );
  }
  return (
    <Sheet open onOpenChange={(next) => !next && void close()}>
      <SheetContent side="right" className="w-[min(56rem,calc(100vw-2rem))] gap-0 overflow-y-auto p-0 sm:max-w-none" data-dd-interview={interviewId}>
        {content}
      </SheetContent>
    </Sheet>
  );
}

/** A save's time as the footer says it. */
function formatClock(at: number): string {
  return new Date(at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/**
 * RNK-16 AC2: a note-taker's text, previewed against the template before anything is written —
 * open as the sheet's first step (D1), folded under *Paste from Granola* once the sections show.
 */
function PasteNotes({
  interviewId,
  sections,
  busy,
  open,
  onTypeInstead,
  onApply
}: {
  interviewId: string;
  /** The interview's template — what names a section the preview fills, by its label. */
  sections: readonly IDdSection[];
  busy: boolean;
  /** Opened as the step the sheet is at; folded otherwise. */
  open: boolean;
  onTypeInstead: () => void;
  /** Applies the text; `filled` are the sections the preview said it fills. True when it landed. */
  onApply: (text: string, filled: string[]) => Promise<boolean>;
}) {
  const [text, setText] = React.useState('');
  const [preview, setPreview] = React.useState<Nullable<IDdNotesPreview>>(null);
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  const labelOf = (code: string) => sections.find((section) => section.code === code)?.label ?? code;

  const ask = async () => {
    setSaid(null);
    try {
      setPreview(await ddApi.previewNotes(interviewId, text));
    } catch (error) {
      setSaid(refusalMessage(error, 'The notes could not be read.'));
    }
  };

  return (
    <details className="grid gap-2 rounded border border-border p-3" data-dd-paste="" open={open || undefined}>
      <summary className="cursor-pointer font-medium">Paste from Granola</summary>
      <AutosizeTextarea
        minRows={6}
        className="mt-2 max-h-96 overflow-y-auto"
        aria-label="Note-taker text"
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          setPreview(null);
        }}
      />
      <div className="mt-2 flex flex-wrap gap-2">
        <Button key="preview" type="button" variant="outline" size="sm" disabled={text.trim() === ''} onClick={() => void ask()}>
          Preview
        </Button>
        <Button
          key="apply"
          type="button"
          size="sm"
          disabled={busy || preview === null}
          onClick={() =>
            void onApply(text, Object.keys(preview?.sections ?? {})).then((landed) => {
              // A refused import keeps the pasted text, to be fixed and applied again.
              if (!landed) return;
              setText('');
              setPreview(null);
            })
          }
        >
          Apply
        </Button>
        {open && (
          <Button key="by-hand" type="button" variant="ghost" size="sm" onClick={onTypeInstead}>
            Type by hand
          </Button>
        )}
      </div>
      {said && <p role="alert">{said}</p>}
      {preview && (
        <div className="mt-2 grid gap-1" aria-label="Notes preview">
          {Object.entries(preview.sections).map(([code, body]) => (
            <PreviewLine key={code} label={labelOf(code)} body={body} />
          ))}
          {preview.unmatched && <p className="text-muted-foreground">Kept apart, no section took it: {preview.unmatched.length} characters.</p>}
          {preview.missing.length > 0 && (
            <p className="text-muted-foreground">No heading for: {preview.missing.map(labelOf).join(', ')}.</p>
          )}
        </div>
      )}
    </details>
  );
}

/** One section the preview fills — its first 160 characters, the rest a press away. */
function PreviewLine({ label, body }: { label: string; body: string }) {
  const [whole, setWhole] = React.useState(false);
  const long = body.length > 160;
  return (
    <p className="whitespace-pre-wrap">
      <span className="font-medium">{label}</span>: {long && !whole ? `${body.slice(0, 160)}…` : body}
      {long && (
        <Button type="button" variant="link" size="sm" className="h-auto px-1" onClick={() => setWhole(!whole)}>
          {whole ? 'Show less' : 'Show all'}
        </Button>
      )}
    </p>
  );
}

function History({ interview }: { interview: IDdInterview }) {
  return (
    <details>
      <summary className="cursor-pointer font-medium">History</summary>
      <ul className="mt-2 grid gap-1">
        {interview.history.map((event, index) => (
          <li key={index} className="flex flex-wrap gap-2">
            <span className="tabular-nums">{ddWhen(event.at)}</span>
            <span className="font-medium">{event.kind}</span>
            <span className="text-muted-foreground">{event.actor.email}</span>
            {/* A booking's note follows its date on the next line (SR-424). */}
            {event.detail && <span className="whitespace-pre-wrap">{event.detail}</span>}
          </li>
        ))}
      </ul>
    </details>
  );
}
